import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { colors } from '../../constants/constants';
import { selectFrameworkControlActivityList } from '../../redux/reducers/activityReducer';
import { selectDescription, selectTitle } from '../../redux/reducers/catalogsReducer';
import {
  ApplicabilityReasons,
  ImplementationStatus,
  InapplicabilityReasons,
  saveUnsavedChanges,
  selectScrollPosition,
  selectScrollReferenceId,
  selectSelectedFrameworkId,
  setControlItemInFocus,
  updateGroups,
} from '../../redux/reducers/frameworkReducer';
import DocumentLink from '../DocumentLink';
import ActivityModal from '../activities/ActivityModal';
import AddContactModal from '../organization/AddContactModal';
import Activity from './Activity';
import OwnerSelector from './OwnerSelector';

function OperationalControl({ control, editMode, scrollReference }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const scrollReferenceId = useSelector(selectScrollReferenceId);
  const scrollPosition = useSelector(selectScrollPosition);
  const selectedFrameworkId = useSelector(selectSelectedFrameworkId);
  const activities = useSelector((state) => selectFrameworkControlActivityList(state, control.id));
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => () => {
    dispatch(saveUnsavedChanges(control.id));
  }, []);

  const memoTextChange = useCallback((event) => {
    const { name, value } = event.target;
    const newDetails = {
      ...control,
      [name]: value,
    };
    dispatch(updateGroups(newDetails));
  }, [control]);

  const handleTextChange = useMemo(
    () => debounce(memoTextChange, 150),
    [memoTextChange],
  );

  const handleModalClose = () => {
    setShowPersonModal(false);
    setShowActivityModal(false);
  };

  const handlePersonAdd = () => {
    setShowPersonModal(true);
  };

  const handleAddActivity = () => {
    setShowActivityModal(true);
  };

  const toggleShowActivities = () => {
    setShowActivities(!showActivities);
  };

  const toggleShowDetails = () => {
    setShowDetails(!showDetails);
  };

  const toggleApplicability = () => {
    const newDetails = {
      ...control,
      applicable: !control.applicable,
      applicabilityReasons: [],
    };
    dispatch(updateGroups(newDetails));
  };

  const handleReasonChange = (value) => {
    const reasons = control.applicabilityReasons.includes(value)
      ? control.applicabilityReasons.filter((reason) => reason !== value)
      : [...control.applicabilityReasons, value];
    const newDetails = {
      ...control,
      applicabilityReasons: reasons,
    };
    dispatch(updateGroups(newDetails));
  };

  const getChipLabel = (reason) => {
    let label;
    if (control.applicable) {
      label = `${t(`control.evaluation.applicability.${reason}Desc`)} (${t(`control.evaluation.applicability.${reason}`)})`;
    } else {
      label = `${t(`control.evaluation.inApplicability.${reason}Desc`)}`;
    }
    return label;
  };

  const handleAddLink = () => {
    const newLink = {
      id: uuidv4(),
      documentName: '',
      link: '',
    };
    const newDetails = {
      ...control,
      links: [...control.links, newLink],
    };
    dispatch(updateGroups(newDetails));
  };

  const handleUpdateDocument = (documentData) => {
    const newDetails = {
      ...control,
      links: control.links.map((link) => (link.id === documentData.id ? documentData : link)),
    };
    dispatch(updateGroups(newDetails));
  };

  const handleDeleteDocument = (id) => {
    const newDetails = {
      ...control,
      links: control.links.filter((link) => (link.id !== id)),
    };
    dispatch(updateGroups(newDetails));
  };

  const handlePersonChange = (value, _) => {
    const newDetails = {
      ...control,
      owner: value,
    };
    dispatch(updateGroups(newDetails));
  };

  const handleButtonGroupSelect = (name, value) => {
    const newDetails = { ...control };
    newDetails[name] = value;
    dispatch(updateGroups(newDetails));
  };

  const title = useSelector((state) => selectTitle(state, control.id));
  const description = useSelector((state) => selectDescription(state, control.id));

  const scrollFocusRef = useRef(null);

  useEffect(() => {
    /*     This handles highlightin the control "in focus" on the sidebar. 
    Margin includes an initial offset for header + desired "sensitivity" for scrolling focus. */
    const margin = 550;
    if (scrollPosition + margin >= scrollFocusRef.current.offsetTop) {
      dispatch(setControlItemInFocus(control.id));
    }
  }, [scrollPosition, control.id]);

  return (
    <>
      {showActivityModal && (
        <ActivityModal
          handleClose={handleModalClose}
          activity={{ frameworkId: selectedFrameworkId, controlIds: [control.id] }}
        />
      )}
      {showPersonModal && (
        <AddContactModal handleClose={handleModalClose} />
      )}
      <Paper ref={scrollFocusRef} elevation={4} sx={{ p: 2 }}>
        <div ref={scrollReferenceId === control.id ? scrollReference : null} />
        <Stack spacing={2}>
          <Stack>
            <Typography variant="bold">{title}</Typography>
            {description !== '' && (
              <Card variant="controlDetails">
                <CardContent variant="controlDetailsContent">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InfoIcon color="primary" />
                    <Typography
                      onClick={toggleShowDetails}
                      variant="body1"
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          color: colors.mainGreen,
                        },
                      }}
                    >
                      {t('framework.controls.viewDescription')}
                    </Typography>
                    <IconButton onClick={toggleShowDetails}>
                      {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>
                </CardContent>
                <Collapse in={showDetails} unmountOnExit>
                  <CardContent variant="controlDetailsContent">
                    <Typography variant="body2" sx={{ color: colors.grey, px: 1 }}>
                      {description}
                    </Typography>
                  </CardContent>
                </Collapse>
              </Card>
            )}
          </Stack>
          <Stack spacing={editMode ? 3 : 2}>
            {editMode ? (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>{t('framework.operational.isControlApplicable')}</Typography>
                  <Switch checked={control.applicable} onChange={toggleApplicability} />
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {control.applicable ? (
                    ApplicabilityReasons.list().map((reason) => (
                      <Chip
                        key={reason}
                        size="medium"
                        label={getChipLabel(reason)}
                        color={control.applicabilityReasons.includes(reason) ? 'primary' : 'default'}
                        onClick={() => handleReasonChange(reason)}
                      />
                    ))
                  ) : (
                    InapplicabilityReasons.list().map((reason) => (
                      <Chip
                        key={reason}
                        size="medium"
                        label={getChipLabel(reason)}
                        color={control.applicabilityReasons.includes(reason) ? 'primary' : 'default'}
                        onClick={() => handleReasonChange(reason)}
                      />
                    ))
                  )}
                </Stack>
              </Stack>
            ) : (
              <Stack>
                <Typography>
                  {control.applicable
                    ? t('framework.operational.controlApplicable')
                    : t('framework.operational.controlNotApplicable')}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {control.applicabilityReasons.map((reason) => (
                    <Chip
                      key={reason}
                      label={getChipLabel(reason)}
                      color="primary"
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            {editMode ? (
              <TextField
                key={control.applicable ? 'description' : 'exclusionJustification'}
                size="small"
                name={control.applicable ? 'description' : 'exclusionJustification'}
                defaultValue={control.applicable
                  ? control.description
                  : control.exclusionJustification}
                onChange={handleTextChange}
                multiline
                rows="4"
                InputLabelProps={{
                  shrink: true,
                }}
                label={control.applicable
                  ? t('framework.controls.description')
                  : t('framework.operational.exclusionJustification')}
                sx={{ backgroundColor: 'white' }}
              />
            ) : (
              <Stack>
                <Typography variant="body1">
                  {control.applicable
                    ? t('framework.controls.description')
                    : t('framework.operational.exclusionJustification')}
                </Typography>
                <Typography variant="body2">
                  {(control.applicable
                    ? control.description
                    : control.exclusionJustification) || '-'}
                </Typography>
              </Stack>
            )}
            {control.applicable && (
              <Stack width="50%">
                <OwnerSelector
                  id={control.id}
                  handlePersonChange={handlePersonChange}
                  handleAddNew={handlePersonAdd}
                  owner={control.owner}
                  editMode={editMode}
                />
              </Stack>
            )}
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>{t('framework.controls.documentationLink')}</Typography>
                <Button size="small" variant="outlined" onClick={handleAddLink} endIcon={<AddIcon />}>
                  {t('framework.controls.addLink')}
                </Button>
              </Stack>
              {control.links.map((link) => (
                <DocumentLink
                  key={link.documentName}
                  document={link}
                  handleDeleteDocument={handleDeleteDocument}
                  handleUpdateDocument={handleUpdateDocument}
                />
              ))}
            </Stack>
            {control.applicable && (
              <>
                <Stack spacing={0.5}>
                  <Typography>{t('framework.controls.status')}</Typography>
                  <ButtonGroup
                    fullWidth
                    variant="outlined"
                    subtype="buttonGroupSelect"
                    disableElevation
                    color="secondary"
                    sx={{ height: '30px' }}
                  >
                    {ImplementationStatus.list().map((status) => (
                      <Button
                        variant={control.implementationStatus === status ? 'contained' : 'outlined'}
                        subtype="buttonGroupSelect"
                        key={status}
                        disabled={!editMode}
                        color="secondary"
                        onClick={() => handleButtonGroupSelect('implementationStatus', status)}
                        sx={{ minWidth: 'fit-content' }}
                      >
                        {t(`framework.controls.statusType.${status}`)}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Stack>
                <Stack spacing={1}>
                  <Stack variant="frameworkButtonRow" direction="row">
                    {control.activities.length === 0 ? (
                      <Stack>
                        <Typography>{t('framework.controls.relatedActivities')}</Typography>
                        <Typography variant="body2">{t('framework.controls.noRelatedActivities')}</Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center">
                        <Typography>
                          {`${t('framework.controls.relatedActivities')} (${control.activities.length})`}
                        </Typography>
                        <IconButton onClick={toggleShowActivities}>{showActivities ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                      </Stack>
                    )}
                    <Button size="small" variant="outlined" onClick={handleAddActivity} endIcon={<AddIcon />}>
                      {t('framework.controls.addActivity')}
                    </Button>
                  </Stack>
                  {showActivities && activities.map((activity) => (
                    <Activity key={activity.id} activity={activity} />
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}
export default OperationalControl;
